import { any, func } from "prop-types";
import React, { Fragment, useState, useEffect } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import {
  markDocumentRead as markRead,
  submitRoyalMailOnboarding,
} from "../../redux/actions";
import ReadingStatus from "../ReadingListDownload/reading_status";
import { downloadRoyalMailForm } from "../../api/profileApi";
import ProfileImageUpload from "../../containers/ProfileImage";
import OnboardingListItem from "../OnboardingListItem/OnboardingListItem";
import * as style from "../ReadingListDownload/styles.module.scss";
import * as styles from "./styles.module.scss";
import Spinner from "../../common/Spinner";
import { Button } from "../../common";

const Onboarding = ({ appData, submitRoyalMailOnboardingFunc }) => {
  const { royalMailOnboarding } = appData;
  const submitted =
    royalMailOnboarding &&
    royalMailOnboarding.submitted &&
    royalMailOnboarding.addSF;
  const idCardUploaded = royalMailOnboarding && royalMailOnboarding.idCardImage;
  const [loading, setLoading] = useState(false);
  useEffect(() => setLoading(false), [submitted]);

  const trafficFormComplete =
    royalMailOnboarding && royalMailOnboarding.traffic;

  const readingListWithStatus = [
    {
      status:
        royalMailOnboarding && royalMailOnboarding.securityForm
          ? ReadingStatus.Done
          : ReadingStatus.Todo,
      title: "Security Checks Consent",
      link: "security-form",
    },
    {
      status:
        royalMailOnboarding && royalMailOnboarding.addSF
          ? ReadingStatus.Done
          : ReadingStatus.Todo,
      title: "Additional Security Checks Consent Form",
      link: "additional-security-form",
    },
    {
      status:
        royalMailOnboarding && royalMailOnboarding.previousAddresses
          ? ReadingStatus.Done
          : ReadingStatus.Todo,
      title: "Additional Previous Address",
      description:
        "You do not have to complete this form if you have lived at your current address for a full 5 years.",
      link: "previous-address-form",
    },
    {
      status:
        royalMailOnboarding && royalMailOnboarding.declarationForm
          ? ReadingStatus.Done
          : ReadingStatus.Todo,
      title: "Personal Declaration",
      link: "declaration-form",
    },
    {
      status:
        royalMailOnboarding && royalMailOnboarding.fiveYearForm
          ? ReadingStatus.Done
          : ReadingStatus.Todo,
      title: "5 Year Interview",
      link: "5-year-form",
    },
    {
      status:
        royalMailOnboarding && royalMailOnboarding.inductionForm
          ? ReadingStatus.Done
          : ReadingStatus.Todo,
      title: "Induction Confirmation",
      description:
        "Please download and read the Induction Booklet before completing this form.",
      link: "induction-form",
    },
  ];

  const canSubmit = readingListWithStatus
    .filter((el) => el.title !== "Additional Previous Address")
    .every((doc) => doc.status === ReadingStatus.Done);

  const handleSubmit = () => {
    setLoading(true);
    let data;
    // exclude images and all other data to be submitted with forms
    data = {
      addSF: appData.royalMailOnboarding.addSF,
      declarationForm: appData.royalMailOnboarding.declarationForm,
      fiveYearForm: appData.royalMailOnboarding.fiveYearForm,
      inductionForm: appData.royalMailOnboarding.inductionForm,
      securityForm: appData.royalMailOnboarding.securityForm,
    };
    if (appData.royalMailOnboarding.previousAddresses) {
      data = {
        ...data,
        previousAddresses: appData.royalMailOnboarding.previousAddresses,
      };
    }
    submitRoyalMailOnboardingFunc(data);
  };

  const triggerDownload = () => {
    setLoading(true);
    downloadRoyalMailForm().then((response) => {
      const link = document.createElement("a");
      const blob = new Blob([response.data], {
        type: "application/pdf",
      });
      link.href = window.URL.createObjectURL(blob);
      link.download = `royal_mail_forms.pdf`;
      link.click();
      setLoading(false);
    });
  };

  const determineStatus = (status) => {
    if (submitted) {
      return 3;
    }
    if (status === 2) {
      return 4;
    }
    return status;
  };

  return (
    <div className="container text-bluishGray pb-2">
      <header className="row">
        <div className="col-12">
          <h2 className="section-header pt-2">Royal Mail Onboarding</h2>
          <p className="section-body p-1">
            Please complete all of the below forms. Once you have completed all
            forms, please click on the Submit button to finish your onboarding
            process. Once you have submitted your forms you will be unable to
            edit them. If you have any queries about the forms, please speak to
            your line manager.
          </p>
        </div>
      </header>
      <div className="row">
        <div className="col-lg-6">
          <div className={`section-body pt-1 bg-white`}>
            <h5 className="px-1">Forms to Complete</h5>
            {readingListWithStatus.map((elem) => (
              <OnboardingListItem
                title={elem.title}
                status={determineStatus(elem.status)}
                key={elem.title}
                subtitle={elem.description}
                btnText={
                  elem.status === ReadingStatus.Todo ? "Enter Details" : "Edit"
                }
                url={`/profile/edit/royal-mail-onboarding/${elem.link}`}
              />
            ))}
            <div className={style.buttonContainer}>
              {!submitted ? (
                <Fragment>
                  <div className={styles.submitButtonDesktop}>
                    Please click on the Submit button for your forms to be sent
                    to HRGO.
                  </div>
                  <Button
                    classes={`btn btn-primary ${style.buttonSubmit}`}
                    disabled={canSubmit && !loading ? "" : "disabled"}
                    onClick={handleSubmit}
                  >
                    {loading ? (
                      <Fragment>
                        Submit <Spinner />
                      </Fragment>
                    ) : (
                      "Submit"
                    )}
                  </Button>
                </Fragment>
              ) : (
                <button
                  className="btn btn-primary"
                  onClick={triggerDownload}
                  disabled={loading ? "disabled" : ""}
                >
                  {loading ? (
                    <Fragment>
                      Download Completed Forms <Spinner />
                    </Fragment>
                  ) : (
                    "Download Completed Forms"
                  )}
                </button>
              )}
            </div>
          </div>
          <div className={`section-body bg-white py-1 my-1 mt-lg-2`}>
            <OnboardingListItem
              title="Road Traffic Collisions"
              status={trafficFormComplete ? 3 : 1}
              key="Traffic"
              btnText="Complete"
              url={`/profile/edit/royal-mail-onboarding/traffic`}
              loading={false}
              downloadingDisabled={true}
            />
          </div>
        </div>
        <div className="col-lg-6">
          <div className={`section-body bg-white py-1 my-1 mt-lg-0`}>
            <h5 className="px-1">Reading List</h5>
            <OnboardingListItem
              title="Induction Booklet"
              btnText="Download"
              handleClick={() => {
                window.open(
                  "https://www.hrgo.co.uk/media/7018/royal-mail-induction-booklet.pdf",
                  "_blank"
                );
              }}
            />
            <OnboardingListItem
              title="Speak Up"
              btnText="Download"
              handleClick={() => {
                window.open(
                  "https://www.hrgo.co.uk/media/7879/speak-up-poster-final.pdf",
                  "_blank"
                );
              }}
            />
          </div>
          <div className={`section-body bg-white p-1 my-1 mt-lg-0`}>
            <h5>Supporting Document</h5>
            <p>
              To support your application we require an image of one of the
              following, please upload below:
            </p>
            <ul>
              <li>National Identity Card</li>
              <li>Biometric Residence Permit</li>
              <li>Picture & Information Page of your Passport</li>
              <li>Driving Licence</li>
            </ul>
            {idCardUploaded && (
              <div className="rounded-circle d-flex justify-content-center">
                <img
                  style={{ maxWidth: "100%", width: "auto", height: "auto" }}
                  src={`data:image/jpg;base64,${royalMailOnboarding.idCardImage.fileContent}`}
                />
              </div>
            )}
            <ProfileImageUpload
              showHeader={false}
              imageFileKey="royalMailIdCard"
              redirectAfterSave={false}
            />
          </div>
          <div className={`section-body bg-white p-1 my-1 mt-lg-0`}>
            <h5>Image for Royal Mail ID</h5>
            <p>
              In order for us to create your Royal Mail identity card, please
              upload a picture of yourself. The photo needs to be a colour image
              of just your face and shoulders. Please only upload JPG or PNG
              file types.
            </p>
            {royalMailOnboarding && royalMailOnboarding.idImage && (
              <div className="rounded-circle d-flex justify-content-center">
                <img
                  style={{ maxWidth: "100%", width: "auto", height: "auto" }}
                  src={`data:image/jpg;base64,${royalMailOnboarding.idImage.fileContent}`}
                />
              </div>
            )}
            <ProfileImageUpload
              showHeader={false}
              imageFileKey="royalMailId"
              redirectAfterSave={false}
            />
          </div>
          <div className="w-100 d-flex justify-content-end mt-2">
            <Link to="/profile" className="btn btn-primary px-3">
              Back to My Account
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

Onboarding.propTypes = {
  appData: any,
  submitRoyalMailOnboardingFunc: func,
};

const mapStateToProps = (state) => ({
  appData: state.core.app,
});

const mapDispatchToProps = {
  markRead,
  submitRoyalMailOnboardingFunc: submitRoyalMailOnboarding,
};

const withRedux = connect(mapStateToProps, mapDispatchToProps);

export default withRedux(Onboarding);
